import { c as _c } from "react/compiler-runtime";
import { Stack, Typography, Divider, Box } from '@mui/material';
import type { GetGcsServiceAgentResponse } from '@repo/api-gw-sdk';
import { Provider } from '@repo/api-gw-sdk';
import { BulletItem } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/bulletItem';
import { NumericBulletSection } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/numericBulletSection';
import { Loader } from '@/components/shared/loader';
import { useDAL } from '@/contexts/useDAL';
import type { BackupVault } from '@/data/vaults/backupVault';
import { getDocsUrl } from '@/docs/docsUrls';
import { generateGcpKmsCommands } from './gcpKmsCommands';
import { BottomDialog } from '../dialogs/bottomDialog';
import { FormTextField } from '../form/formTextField';
import { getCloudFieldConfig } from '../form/validation/cloudFieldValidation';
import { PanelWrapper } from '../panels/panelWrapper';
import { CopyableCode } from '../shared/copyableCode';
import { ReadMoreLink } from '../shared/readMoreLink';
const gcpKmsKeyConfig = getCloudFieldConfig(Provider.Gcp, 'kmsKeyResourceName');
function GcpCmekDescription() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Typography variant="body1" component="span">Set the Cloud KMS key to encrypt snapshots with. You're responsible for securing the key from unauthorized access and deletion. Without this key, snapshots can't be retrieved from the vault.{" "}<ReadMoreLink href={getDocsUrl("vaultSelfManagedKeys")} /></Typography>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export function GcpKmsKeyEditorPanel({
  open,
  onClose,
  data,
  setData,
  scanningAccountId
}: {
  open: boolean;
  onClose: () => void;
  data: BackupVault;
  setData: (data: BackupVault) => void;
  scanningAccountId: string;
}) {
  const dal = useDAL();
  const {
    data: serviceAgent,
    isLoading
  } = dal.cloudAccounts.scanning.useGetGcsServiceAgent(scanningAccountId);
  const encryptionKey = data.vaultAttributes.gcp?.encryptionKey ?? '';
  const isKeyValid = encryptionKey.trim().length > 0;
  const handleKeyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setData({
      ...data,
      vaultAttributes: {
        ...data.vaultAttributes,
        gcp: {
          ...data.vaultAttributes.gcp,
          encryptionKey: event.target.value
        }
      }
    });
  };
  return <BottomDialog open={open}>
      <PanelWrapper header={{
      title: 'Set Cloud KMS Key',
      subtitle: <GcpCmekDescription />,
      fullWidthSubtitle: false,
      onClose: onClose
    }} footer={{
      primaryButton: {
        text: 'Done',
        'data-testid': 'done-button',
        onClick: onClose,
        disabled: !isKeyValid
      },
      secondaryButton: {
        text: 'Cancel',
        'data-testid': 'cancel-button',
        onClick: onClose
      }
    }}>
        {isLoading ? <Loader /> : <Box sx={{
        padding: '40px min(100px,6vw)',
        width: '100%',
        height: 'auto'
      }}>
            <Stack divider={<Divider sx={{
          borderStyle: 'dashed'
        }} />} sx={{
          gap: '40px'
        }}>
              <KeyDetailsSection encryptionKey={encryptionKey} onChange={handleKeyChange} />
              {serviceAgent && <KmsAccessSection encryptionKey={encryptionKey} serviceAgent={serviceAgent ?? {}} />}
            </Stack>
          </Box>}
      </PanelWrapper>
    </BottomDialog>;
}
function KeyDetailsSection(t0) {
  const $ = _c(3);
  const {
    encryptionKey,
    onChange
  } = t0;
  let t1;
  if ($[0] !== encryptionKey || $[1] !== onChange) {
    t1 = <NumericBulletSection bulletNumber={1} title="Set key details"><BulletItem leftContent={<FormTextField title="CMEK key resource name" placeholder={gcpKmsKeyConfig.placeholder} value={encryptionKey} onChange={onChange} validation={gcpKmsKeyConfig.validation} />} /></NumericBulletSection>;
    $[0] = encryptionKey;
    $[1] = onChange;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function KmsAccessSection(t0) {
  const $ = _c(8);
  const {
    encryptionKey,
    serviceAgent
  } = t0;
  let t1;
  if ($[0] !== encryptionKey || $[1] !== serviceAgent.gcsServiceAgent || $[2] !== serviceAgent.restoreServiceAccounts || $[3] !== serviceAgent.scanningServiceAccount) {
    t1 = generateGcpKmsCommands(encryptionKey, {
      scanningServiceAccount: serviceAgent.scanningServiceAccount,
      gcsServiceAgent: serviceAgent.gcsServiceAgent,
      restoreServiceAccounts: serviceAgent.restoreServiceAccounts
    });
    $[0] = encryptionKey;
    $[1] = serviceAgent.gcsServiceAgent;
    $[2] = serviceAgent.restoreServiceAccounts;
    $[3] = serviceAgent.scanningServiceAccount;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const kmsCommands = t1;
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Box />;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== kmsCommands) {
    t3 = <NumericBulletSection bulletNumber={2} title="Grant KMS key access for vault"><BulletItem title="Grant the required service accounts access to your KMS key. These permissions enable Eon to create, manage, and restore encrypted data in the vault." leftContent={t2} rightContent={<CopyableCode title="vault_kms_perm.sh" code={kmsCommands} />} /></NumericBulletSection>;
    $[6] = kmsCommands;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}